import { useSetAtom } from 'jotai'
import { match } from 'ts-pattern'
import {
  Text,
  BorderRadius4,
  Button,
  IconCheckmark,
  CustomerLogo,
  IconEmail,
  IconFlag,
  GreyGrey10,
  GreyGrey70,
  GreyGrey90,
  Link,
  Primary13Regular,
  Primary14SemiBold,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'
import { NodeTimeline } from 'Automations/components'
import {
  CancelledItem,
  TerminalStateItem
} from 'Automations/nodeDefinitions/executionSummaryUtils'
import { InboxPreview } from 'Automations/components'
import { manualTaskProcessorTaskIdAtom } from 'Automations/watchtower/atoms'
import { useManualTask } from 'Automations/watchtower/inbox/manualTasks/hooks/useManualTask'
import { MenuManualTaskActions } from 'Automations/components'
import { getLogoSrc } from 'Customer/utils/logo'
import {
  CustomerInfoChangeSection,
  CustomerInfoReviewInput,
  CustomerInfoReviewOutcome,
  CustomerInfoReviewViewModel,
  CustomerSnapshot
} from '../../types'
import {
  getChangeReviewTitle,
  getChangeSummaryTitle,
  getChangedContactIds,
  mapInputToViewModel
} from '../../customerInfoDiff'
import { useDismissCustomerInfoReviewTask } from '../../hooks/useDismissCustomerInfoReviewTask'
import { SourceEmailPreview } from 'Automations/watchtower/inbox/manualTasks/components'
import { CustomerCard } from '../reviewContent/CustomerCard'
import { ContactCard } from '../reviewContent/ContactCard'

/**
 * Right-hand "details / logs" pane for a customer-info review task: the agent's
 * summary of what it proposed, with the proposed change shown as cards (the
 * customer card and/or billing-contact cards), plus the primary "Review changes"
 * action that opens the full-screen review modal.
 */
export const CustomerInfoReviewPreview = ({ taskId }: { taskId: string }) => {
  const { manualTask } = useManualTask({ taskId })
  const setProcessorTaskId = useSetAtom(manualTaskProcessorTaskIdAtom)

  const input = manualTask?.input as CustomerInfoReviewInput | undefined

  if (!input) {
    return <InboxPreview.EmptyState />
  }

  const { customer, summary, source, sections, before, after } =
    mapInputToViewModel(input)

  // Once the task is resolved (confirmed or dismissed) there's nothing left to
  // action, so the review/dismiss/cancel controls are replaced by a passive
  // link through to the customer — mirroring how the execution summary swaps
  // "Review quote" for "View quote" on completion.
  const isPending = manualTask?.status === 'PENDING'

  // Terminal timeline step: once resolved, the timeline is capped with the
  // outcome (applied / dismissed / cancelled), reusing the shared terminal-state
  // items so it reads consistently with the automation execution summaries. The
  // outcome is recorded under the task's `output` on completion; a `CANCELLED`
  // status has no outcome (the task was cancelled externally). Rendered as the
  // last step, so it carries no stem (`isLatestNodeExecution`).
  const outcome = manualTask?.output?.outcome as
    | CustomerInfoReviewOutcome['outcome']
    | undefined
  const resolvedStep = match({ status: manualTask?.status, outcome })
    .with({ outcome: 'CONFIRMED' }, () => (
      <TerminalStateItem
        icon={<IconCheckmark />}
        label="Changes applied"
        timestamp={manualTask?.updatedAt}
        isLatestNodeExecution
      />
    ))
    .with({ outcome: 'DISMISSED' }, () => (
      <CancelledItem
        label="Changes dismissed"
        timestamp={manualTask?.updatedAt}
        isLatestNodeExecution
      />
    ))
    .with({ status: 'CANCELLED' }, () => (
      <CancelledItem
        label="Review cancelled"
        timestamp={manualTask?.updatedAt}
        isLatestNodeExecution
      />
    ))
    .otherwise(() => null)

  return (
    <InboxPreview
      header={
        <PreviewHeader
          customer={customer}
          title={getChangeSummaryTitle(sections)}
        />
      }
      actions={
        isPending ? (
          <>
            <DismissButton taskId={taskId} customerName={customer.legalName} />
            <Button
              variant="primary"
              onClick={() => setProcessorTaskId(taskId)}
              data-testid="watchtower.customerInfoReview.reviewButton"
            >
              Review changes
            </Button>
          </>
        ) : (
          <Button
            as={Link.Unstyled}
            to={`/customers/${customer.id}`}
            variant="secondary"
            data-testid="watchtower.customerInfoReview.viewCustomerButton"
          >
            View customer
          </Button>
        )
      }
      headerActions={
        isPending ? <MenuManualTaskActions taskId={taskId} /> : undefined
      }
    >
      <NodeTimeline data-testid="watchtower.customerInfoReview.preview">
        {source && (
          <NodeTimeline.Item icon={<IconEmail />} showStem>
            <NodeTimeline.Title>
              <NodeTimeline.UserAction
                label={`Email forwarded`}
                timestamp={source.receivedAt}
              />
            </NodeTimeline.Title>
            <Box marginBottom="16px">
              <SourceEmailPreview
                source={source}
                data-testid="watchtower.customerInfoReview.preview.emailCard"
              />
            </Box>
          </NodeTimeline.Item>
        )}

        <NodeTimeline.Item icon={<IconFlag />} showStem={Boolean(resolvedStep)}>
          <NodeTimeline.Title>
            <NodeTimeline.UserAction
              label={getChangeReviewTitle(sections)}
              timestamp={manualTask?.createdAt}
            />
          </NodeTimeline.Title>

          <Flex flexDirection="column" gap="12px">
            <Text
              {...Primary13Regular}
              color={GreyGrey70}
              whiteSpace="pre-wrap"
            >
              {summary}
            </Text>
            <ProposedChangeCards
              sections={sections}
              before={before}
              after={after}
            />
          </Flex>
        </NodeTimeline.Item>

        {resolvedStep}
      </NodeTimeline>
    </InboxPreview>
  )
}

/**
 * Header action that dismisses the task (the `DISMISSED` outcome — nothing is
 * applied and the task leaves the inbox). Loading state is driven by the
 * mutation's `isPending` (no local state); while pending it shows a spinner
 * beside the unchanged "Dismiss" label — the app's default `submitting`
 * treatment.
 */
const DismissButton = ({
  taskId,
  customerName
}: {
  taskId: string
  customerName: string
}) => {
  const { dismiss, isPending } = useDismissCustomerInfoReviewTask({
    taskId,
    customerName
  })

  return (
    <Button
      variant="secondary"
      onClick={dismiss}
      submitting={isPending}
      data-testid="watchtower.customerInfoReview.dismissButton"
    >
      Dismiss
    </Button>
  )
}

const PreviewHeader = ({
  customer,
  title
}: {
  customer: CustomerInfoReviewViewModel['customer']
  title: string
}) => (
  <Flex alignItems="center" gap="8px">
    <Center
      borderRadius={BorderRadius4}
      overflow="hidden"
      boxSize="24px"
      background={GreyGrey10}
    >
      <CustomerLogo
        src={getLogoSrc(customer)}
        size={24}
        fallbackSize={16}
        data-testid="watchtower.customerInfoReview.preview.logo"
      />
    </Center>
    <Text {...Primary14SemiBold} color={GreyGrey90}>
      {customer.legalName}: {title}
    </Text>
  </Flex>
)

/**
 * The proposed change rendered as cards, reusing the same visuals as the full
 * review modal so the preview reads consistently with it. Shows the customer
 * card when company details or the billing address changed, and a billing-
 * contact card per contact the change actually added or modified — stacked, so a
 * change touching both shows the customer card above the contact cards. Contacts
 * that merely rode along unchanged in the proposed snapshot are omitted, so the
 * preview reflects only what the change did. A contacts change always yields at
 * least one card: removals aren't diffed (they're unsupported through this flow
 * and never applied), so a billingContacts section implies an added or modified
 * contact.
 */
const ProposedChangeCards = ({
  sections,
  before,
  after
}: {
  sections: CustomerInfoChangeSection[]
  before: CustomerSnapshot
  after: CustomerSnapshot
}) => {
  const changedKeys = new Set(sections.map(section => section.key))
  const hasCustomerInfoChange =
    changedKeys.has('details') || changedKeys.has('billingAddress')
  const hasContactChange = changedKeys.has('billingContacts')
  // Only the contacts the change added or modified — an unchanged contact that
  // merely rode along in the proposed snapshot shouldn't appear as if it were
  // part of the change.
  const changedContactIds = getChangedContactIds(before, after)
  const contacts = (after.contacts ?? []).filter(contact =>
    changedContactIds.has(contact.id)
  )
  // Contacts present in `after` but not `before` are being added by the change.
  const beforeContactIds = new Set((before.contacts ?? []).map(c => c.id))

  return (
    <Flex
      flexDirection="column"
      gap="12px"
      // Cap the cards so they don't stretch the full width of the preview pane;
      // applied to the stack so the customer and contact cards stay aligned.
      maxWidth="314px"
      data-testid="watchtower.customerInfoReview.preview.changeCards"
    >
      {hasCustomerInfoChange && (
        <CustomerCard
          customer={after}
          variant="after"
          data-testid="watchtower.customerInfoReview.preview.customerCard"
        />
      )}
      {hasContactChange &&
        contacts.map(contact => (
          <ContactCard
            key={contact.id}
            contact={contact}
            variant="after"
            companyName={after.legalName}
            showEmptyPlaceholder={false}
            isNew={!beforeContactIds.has(contact.id)}
          />
        ))}
    </Flex>
  )
}
